import { registerLocaleData } from '@angular/common';

// [i18n] Angular Locales
import localeCs from '@angular/common/locales/cs';
import localeDe from '@angular/common/locales/de';
import localeEnGB from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeHu from '@angular/common/locales/hu';
import localeJa from '@angular/common/locales/ja';
import localeKo from '@angular/common/locales/ko';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localeRo from '@angular/common/locales/ro';
import localeRu from '@angular/common/locales/ru';
import localeTr from '@angular/common/locales/tr';
import localeCn from '@angular/common/locales/zh';
import localeIt from '@angular/common/locales/it';

registerLocaleData(localeCs);
registerLocaleData(localeDe);
registerLocaleData(localeEnGB);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeHu);
registerLocaleData(localeJa);
registerLocaleData(localeKo);
registerLocaleData(localePl);
registerLocaleData(localePt);
registerLocaleData(localeRo);
registerLocaleData(localeRu);
registerLocaleData(localeTr);
registerLocaleData(localeCn);
registerLocaleData(localeIt);

import { BrowserModule, EventManager } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { from, Observable } from 'rxjs';
import { FestoAngularModule } from '@festo-ui/angular';
import {
  FSL_VALIDATION_ERROR_MAPPER,
  FslValidationErrorContext,
  FslValidationErrorMapper,
  provideFslTrustedIframeDomains,
  WEB_PUB_SUB_ENDPOINT,
  WebPubSubService,
} from '@festo-ax/smartenance-shared-ui';
import { WEB_PUB_SUB_API_ENDPOINT } from './services/utils.service';

import { environment } from 'environments/environment';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { AppErrorComponent } from './components/app-error/app-error.component';
import { LanguageHeaderInterceptor } from './interceptors/language-header.interceptor';
import { SharedModule } from './modules/shared.module';
import { CustomEventManager } from './utils/custom-event-manager';
import { AppConfig } from './app.config';
import { PageRedirectComponent } from './containers/page-redirect/page-redirect.component';
import { getLocalStorage, getSessionStorage, LOCAL_STORAGE, SESSION_STORAGE } from './services/storage/storage.service';
import { WINDOW, windowFactory } from './utils/window';
import { HttpErrorConvertInterceptor } from './interceptors/http-error-convert-interceptor.service';
import { ApiCacheInterceptor } from './interceptors/api-cache.interceptor';
import { DeletionGuardInterceptor } from './interceptors/deletion-guard.interceptor';
import { AppStoreModule } from './app-store.module';
import { CustomMissingTranslationHandler } from './utils/handlers/custom-missing-translation.handler';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { LicensesEffects } from './effects/licenses';
import * as licensesReducer from 'app/reducers/licenses';
import { FormHelperService } from './services/form-helper.service';
import { ValidationErrors } from '@angular/forms';
import { CookieService } from 'ng2-cookies';
import {
  BearerTokenInterceptor,
  CurrentTenantInterceptor,
  initializeDevAuth,
  initializeIdentity,
  initializeWelcomeBranding,
  SilentTokenRefreshInterceptor,
  DidomiService,
  FwaTrackingService,
  initializeDidomi,
  initializeDidomiRecovery,
  initializeFwaTracking,
  CookieBannerComponent,
  WelcomeBrandingLoaderService,
} from './standalone-core/features';
import { provideTableMemory } from './state-management/global-table-memory/table-memory.provider';

export class HashTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<unknown> {
    /**
     * we exclude use base language for english britain
     */
    if (lang !== 'en-gb' && lang.indexOf('-')) {
      lang = lang.slice(0, 2);
    }
    return from(import(`../assets/i18n/${lang}.json`));
  }
}

export function initializeApp(appConfig: AppConfig): () => Promise<void> {
  return () => appConfig.load();
}

const imports = [
  BrowserModule,
  BrowserAnimationsModule,
  FestoAngularModule,
  SharedModule.forRoot(),
  StoreModule.forFeature(licensesReducer.name, licensesReducer.reducer),
  EffectsModule.forFeature([LicensesEffects]),
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useClass: HashTranslateLoader
    },
    missingTranslationHandler: {
      provide: MissingTranslationHandler,
      useClass: CustomMissingTranslationHandler
    }
  }),
  AppRoutingModule,
  AppStoreModule,
  CookieBannerComponent,
  environment.imports,
];

@NgModule({
  declarations: [AppComponent, PageRedirectComponent],
  imports,
  providers: [
    AppConfig,
    PageNotFoundComponent,
    AppErrorComponent,
    provideHttpClient(withInterceptorsFromDi()),
    provideFslTrustedIframeDomains(environment.trustedDomain ?? []),
    { provide: LOCALE_ID, useFactory: (cookie: CookieService) => cookie.get('ecn-lang-code') || 'en', deps: [CookieService] },
    { provide: WINDOW, useFactory: (): Window | null => windowFactory() },
    { provide: LOCAL_STORAGE, useFactory: (): Storage | null => getLocalStorage() },
    { provide: SESSION_STORAGE, useFactory: (): Storage | null => getSessionStorage() },
    provideAppInitializer(() => (initializeApp)(inject(AppConfig))()),
    ...(environment.version === 'local' ? [provideAppInitializer(() => initializeDevAuth()())] : []),
    provideAppInitializer(() => initializeWelcomeBranding()()),
    provideAppInitializer(() => initializeIdentity()()),
    provideAppInitializer(() => initializeDidomi(inject(DidomiService), inject(WelcomeBrandingLoaderService))()),
    provideAppInitializer(() => initializeDidomiRecovery(inject(DidomiService), inject(WelcomeBrandingLoaderService))()),
    provideAppInitializer(() => initializeFwaTracking(inject(FwaTrackingService), inject(WelcomeBrandingLoaderService))()),
    { provide: EventManager, useClass: CustomEventManager },
    // for responses order of interceptors flows out from the end to begin, so first in the list will be the last for response
    { provide: HTTP_INTERCEPTORS, useClass: LanguageHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorConvertInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DeletionGuardInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SilentTokenRefreshInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BearerTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CurrentTenantInterceptor, multi: true },
    {
      provide: FSL_VALIDATION_ERROR_MAPPER,
      useFactory: (formHelperService: FormHelperService): FslValidationErrorMapper => {
        return (errors: ValidationErrors, context?: FslValidationErrorContext): string | null => {
          return formHelperService.getErrorMessageFromErrors(errors, context);
        };
      },
      deps: [FormHelperService]
    },
    provideTableMemory(),
    // Web Pub Sub service data
    WebPubSubService,
    { provide: WEB_PUB_SUB_ENDPOINT, useValue: WEB_PUB_SUB_API_ENDPOINT }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
